import { useState, useRef, useLayoutEffect, useMemo, useEffect, useCallback } from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  SortingState,
  ColumnDef,
  ColumnPinningState,
  RowSelectionState,
  ColumnFiltersState,
  getSortedRowModel
} from '@tanstack/react-table'
import { motion, LayoutGroup } from 'framer-motion'
import MFNameCard from './MFNameCard'
import Score from './Score'
import Returns from './Returns'
import SortHeader from './SortHeader'
import { usePinnedIds, useSetPinnedIds } from '../../state-management/fastPinnedRowsStore'
import { usePeerAnalyserFunds } from '../../hooks/usePeerAnalyserFunds'
import { usePeerAnalyserStore } from '../../state-management/peerAnalyserStore'
import { FundsPowerResponse } from '../../types/peer-analyser'
import { useBookmarkFund } from '../../hooks/useBookmarkFund'
import EliteScores from './EliteScores'
import PeerAnalyserUnlock from './PeerAnalyserUnlock'
import { useToast } from '../../state-management/contexts/ToastContext'
import { trackEvent, lightImpactVibrate } from '../../utils/appBridge'
import { associatedColours } from '../../constants'
import { EventNames } from '../../types/misc.types'
import { getCategoryLabel } from '../../config/categoryConfig'
import FilterAndColumnReorderBottomSheet, { ColumnItem } from './FilterAndColumnReorderBottomSheet'
import CategoryAboutBottomSheet, { CATEGORY_ABOUT_HEADING_ID } from './CategoryAboutBottomSheet'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { convertNumberToCurrency, formatNumberWithoutCurrency } from '../../utils/utils'
import { getBottomPadding } from '../../utils/sessionStorage'

const findColumnDefById = (
  columnDefs: ColumnDef<FundsPowerResponse, any>[],
  id: string
): ColumnDef<FundsPowerResponse, any> | undefined => {
  for (const def of columnDefs) {
    if (def.id === id) {
      return def
    }
    // Check if it's a group and recurse
    if ((def as any).columns) {
      // Use 'as any' for now to access .columns property of ColumnGroup
      const found = findColumnDefById((def as any).columns, id)
      if (found) {
        return found
      }
    }
  }
  return undefined
}
interface PeerAnalyserTableProps {
  showFtux?: boolean
  onFtuxComplete?: () => void
}

export function PeerAnalyserTable({ showFtux = false, onFtuxComplete }: PeerAnalyserTableProps) {
  const { data, isLoading, isSuccess } = usePeerAnalyserFunds()
  const selectedCategoryId = usePeerAnalyserStore((state) => state.selectedCategoryId)
  const { showToast } = useToast()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { mutate: bookmarkFund } = useBookmarkFund(selectedCategoryId, (responseData) => {
    // Assuming responseData will have a 'status' or 'code' field
    // that indicates MAX_LIMIT, replace 'MAX_LIMIT_STATUS_CODE'
    // with the actual value from your API response.
    if (responseData?.status === 'MAX_LIMIT') {
      // Adjust this condition based on your API response structure
      showToast({ message: 'You can only pin 3 funds at a time', width: '260px' }) // Customize your message
    } else {
      // Handle successful bookmark/unbookmark if needed
    }
  })

  // Get relevant states from usePeerAnalyserStore
  const isUserElite = usePeerAnalyserStore((state) => state.isUserElite)
  const isSubscribedToPeerAnalyser = usePeerAnalyserStore((state) => state.isSubscribedToPeerAnalyser)
  const isUserExhaustedPASubs = usePeerAnalyserStore((state) => state.isUserExhaustedPASubs)
  const throughFamilyAccess = usePeerAnalyserStore((state) => state.throughFamilyAccess)

  // Get the setter functions from the store
  const setisUserElite = usePeerAnalyserStore((state) => state.setisUserElite)
  const setisSubscribedToPeerAnalyser = usePeerAnalyserStore((state) => state.setisSubscribedToPeerAnalyser)
  const setisUserExhaustedPASubs = usePeerAnalyserStore((state) => state.setisUserExhaustedPASubs)
  const setIsFtuxEnabled = usePeerAnalyserStore((state) => state.setIsFtuxEnabled)
  const setTenureInDays = usePeerAnalyserStore((state) => state.setTenureInDays)
  const setThroughFamilyAccess = usePeerAnalyserStore((state) => state.setThroughFamilyAccess)

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([{ id: 'performance', value: 'IN_FORM' }])
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: ['mfNameCard']
  })
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [showRollingReturnsRank, setShowRollingReturnsRank] = useState(false)
  const [showTrailingReturnsRank, setShowTrailingReturnsRank] = useState(false)
  const [showInFormOnly, setShowInFormOnly] = useState(true)
  const [fundsCount, setFundsCount] = useState<number>(0)
  const headerRef = useRef<HTMLTableSectionElement>(null)
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement }>({})
  const [showRankInNameCard, setShowRankInNameCard] = useState(false)
  const [mfnameColumnWidth, setMfnameColumnWidth] = useState(180)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const [allowAnimation, setAllowAnimation] = useState(false)
  const [isSorting, setIsSorting] = useState(false)
  const [isFiltering, setIsFiltering] = useState(false)
  const [emptyRowsNeeded, setEmptyRowsNeeded] = useState<number>(0)
  const showFilterBottomSheet = usePeerAnalyserStore((state) => state.showFilterBottomSheet)
  const setShowFilterBottomSheet = usePeerAnalyserStore((state) => state.setShowFilterBottomSheet)
  // Power Select_B21: announce sort/filter updates without moving focus
  const [statusMessage, setStatusMessage] = useState('')
  const statusClearRef = useRef<number | null>(null)

  const announceStatus = useCallback((message: string) => {
    setStatusMessage(message)
    if (statusClearRef.current) window.clearTimeout(statusClearRef.current)
    statusClearRef.current = window.setTimeout(() => setStatusMessage(''), 1500)
  }, [])

  // Default column order (excluding mfNameCardGroup which is always first)
  const defaultColumnOrder = [
    'gap0',
    'powerRankColumn',
    'gap1',
    'eliteScores',
    'gap2',
    'rollingReturns',
    'gap3',
    'trailingReturns',
    'gap4',
    'aumChange',
    'gap5',
    'fundAgeAndAum',
    'gap6'
  ]

  // Normalize gap columns: currently a no-op, kept for legacy compatibility/extension
  const normalizeGapColumns = useCallback((order: string[]): string[] => order, [])

  // Ensure boundary gaps are present exactly once
  const ensureBoundaryGaps = useCallback((order: string[]): string[] => {
    const middle = order.filter((id) => id !== 'gap0' && id !== 'gap6')
    return ['gap0', ...middle, 'gap6']
  }, [])

  // Mapping from enum strings to column IDs (same as FilterAndColumnReorderBottomSheet)
  const ENUM_TO_COLUMN_ID_MAP: Record<string, string> = {
    RANK_AND_PERFORMANCE: 'powerRankColumn',
    ELITE_SCORE: 'eliteScores',
    ROLLING_RETURNS: 'rollingReturns',
    TRAILING_RETURNS: 'trailingReturns',
    AUM_CHANGE: 'aumChange',
    AUM_AND_FUND_AGE: 'fundAgeAndAum'
  }

  // Helper function to merge reordered columns with gap columns
  // Gap sizes: gap0, gap1, gap5, gap6 = 12px; gap2, gap3, gap4 = 11px
  // We use gap1 (12px) for all intermediate gaps to maintain consistent spacing
  const mergeWithGapColumns = (reorderedColumns: string[]): string[] => {
    // Remove any gap IDs from the incoming order so we don't duplicate gaps
    const cleanedColumns = reorderedColumns.filter((id) => !id.startsWith('gap'))
    const result: string[] = []

    // Add gap0 before first column (12px)
    result.push('gap0')

    // Add columns with gaps in between
    const internalGaps = ['gap1', 'gap2', 'gap3', 'gap4', 'gap5']
    cleanedColumns.forEach((colId, index) => {
      result.push(colId)
      // Add gap after each column (except the last one)
      if (index < cleanedColumns.length - 1) {
        // Use a distinct gap id for each internal position to keep React keys unique
        const gapId = internalGaps[index] ?? 'gap5'
        result.push(gapId)
      }
    })

    // Add final gap6 after last column (12px)
    result.push('gap6')

    return ensureBoundaryGaps(result)
  }

  // Compute effective display filters: prefer API response, otherwise fall back to global persisted filters
  const effectiveDisplayFilters = useMemo(() => {
    if (data?.peerAnalyserDisplayFilters && data.peerAnalyserDisplayFilters.length > 0) {
      return data.peerAnalyserDisplayFilters
    }
    try {
      const stored = localStorage.getItem('peerAnalyserDisplayFilters_global')
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
        }
      }
    } catch {
      // Ignore JSON / storage errors
    }
    return undefined
  }, [data?.peerAnalyserDisplayFilters])

  // Build column order from peerAnalyserDisplayFilters if available
  const buildColumnOrderFromFilters = useCallback(
    (filters: string[] | undefined): string[] => {
      if (!filters || filters.length === 0) {
        return defaultColumnOrder
      }

      // Map enum strings to column IDs
      const selectedColumnIds = filters
        .map((enumValue) => ENUM_TO_COLUMN_ID_MAP[enumValue])
        .filter((colId): colId is string => !!colId)

      // Build column order with gaps and normalize spacing, ensuring boundary gaps
      return ensureBoundaryGaps(normalizeGapColumns(mergeWithGapColumns(selectedColumnIds)))
    },
    [normalizeGapColumns, ensureBoundaryGaps]
  )

  // Get column order from backend data only (single source of truth)
  const getColumnOrder = useCallback((): string[] => {
    return buildColumnOrderFromFilters(effectiveDisplayFilters)
  }, [effectiveDisplayFilters, buildColumnOrderFromFilters])

  const [columnOrder, setColumnOrder] = useState<string[]>(getColumnOrder())

  // Extract non-gap columns from the full column order for the modal
  const getNonGapColumns = (fullOrder: string[]): string[] => {
    return fullOrder.filter((id) => !id.startsWith('gap'))
  }

  // Update column order when effectiveDisplayFilters changes (backend is source of truth)
  useEffect(() => {
    setColumnOrder(getColumnOrder())
  }, [getColumnOrder])

  const pinnedRowIds = usePinnedIds()
  // No longer need direct access to pin/unpin from here
  // const pinRow = usePinRow();
  // const unpinRow = useUnpinRow();
  const setPinnedIds = useSetPinnedIds()
  const previousCategoryIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isSuccess && data) {
      setisUserElite(data.isUserElite)
      setisSubscribedToPeerAnalyser(data.isSubscribedToPeerAnalyser)
      setisUserExhaustedPASubs(data.isUserExhaustedPASubs)
      setIsFtuxEnabled(data.isFtuxEnabled)
      setTenureInDays(data.tenureInDays)
      setThroughFamilyAccess(data.throughFamilyAccess)
    }
  }, [isSuccess, data, setisUserElite, setisSubscribedToPeerAnalyser, setisUserExhaustedPASubs, setIsFtuxEnabled])

  useEffect(() => {
    if (data?.selectedFundsIsinList) {
      setPinnedIds(data.selectedFundsIsinList)
      // Defer enabling animations until after the initial pinned state is rendered.
      requestAnimationFrame(() => {
        setAllowAnimation(true)
      })
    }
  }, [data, setPinnedIds])

  // Reset filter when category changes (not when data changes due to pinning/unpinning)
  // If there are IN_FORM funds, filter to show only them
  // If there are no IN_FORM funds, show all funds (no filter)
  useEffect(() => {
    const categoryChanged =
      previousCategoryIdRef.current !== undefined && previousCategoryIdRef.current !== selectedCategoryId
    const isInitialLoad = previousCategoryIdRef.current === undefined && data?.fundsPowerResponseList

    // Only reset filter when category actually changes or on initial load
    if (categoryChanged || isInitialLoad) {
      if (data?.fundsPowerResponseList) {
        const hasInFormFunds = data.fundsPowerResponseList.some(
          (fund: FundsPowerResponse) => fund.powerPerformance === 'IN_FORM'
        )

        if (hasInFormFunds) {
          setShowInFormOnly(true)
          setColumnFilters([{ id: 'performance', value: 'IN_FORM' }])
        } else {
          // No IN_FORM funds, show all funds
          setShowInFormOnly(false)
          setColumnFilters([])
        }
      } else {
        // No data yet, set default to show IN_FORM (will be adjusted when data loads)
        setShowInFormOnly(true)
        setColumnFilters([{ id: 'performance', value: 'IN_FORM' }])
      }
    }

    // Update the ref to track the current category
    previousCategoryIdRef.current = selectedCategoryId
  }, [selectedCategoryId, data])

  // Handle user scroll to dismiss FTUX
  const [isFtuxFading, setIsFtuxFading] = useState(false)

  const handleUserScroll = useCallback(() => {
    if (showFtux && !isFtuxFading) {
      setIsFtuxFading(true)
      // Delay the actual dismissal to allow fade animation
      setTimeout(() => {
        onFtuxComplete?.()
        setIsFtuxFading(false)
      }, 300) // Match the transition duration
    }
  }, [showFtux, onFtuxComplete, isFtuxFading])

  // Dynamic height calculation
  useLayoutEffect(() => {
    const calculateMaxHeight = () => {
      const viewportHeight = window.innerHeight

      // Get the table container's offset from top of viewport
      const tableContainer = tableContainerRef.current
      if (tableContainer) {
        const containerTop = tableContainer.getBoundingClientRect().top
        // Reserve some space for potential margins/padding (adjust as needed)
        const reservedSpace = 0
        const calculatedMaxHeight = viewportHeight - containerTop - reservedSpace
        setMaxHeight(Math.max(calculatedMaxHeight, 200)) // Minimum height of 200px
      }
    }

    calculateMaxHeight()

    // Recalculate on window resize
    window.addEventListener('resize', calculateMaxHeight)
    return () => window.removeEventListener('resize', calculateMaxHeight)
  }, [])

  // Helper function to handle header clicks with scroll to top
  const handleHeaderClick = useCallback(
    (sortHandler: ((event: unknown) => void) | undefined, columnLabel?: string, column?: any) => {
      return (e: React.MouseEvent) => {
        lightImpactVibrate()

        // Track column sort with action
        if (columnLabel && column) {
          const currentSortState = column.getIsSorted()
          // Determine the action that will be performed after toggle
          let sortAction: string
          let statusAnnouncement: string
          if (currentSortState === false) {
            sortAction = 'Sort Ascending'
            statusAnnouncement = `Sorted by ${columnLabel}, ascending`
          } else if (currentSortState === 'asc') {
            sortAction = 'Sort Descending'
            statusAnnouncement = `Sorted by ${columnLabel}, descending`
          } else {
            sortAction = 'Remove Sort'
            statusAnnouncement = `Sort removed from ${columnLabel}`
          }

          trackEvent({
            eventName: EventNames.ButtonTap,
            payload: {
              ScreenName: 'PeerAnalyser',
              buttonLabel: `${columnLabel} - ${sortAction}`,
              eventId: selectedCategoryId
            }
          })
          announceStatus(statusAnnouncement)
        }

        // Call the original sort handler
        if (sortHandler) {
          sortHandler(e)
        }
        // Scroll to top
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = 0
        }
      }
    },
    [selectedCategoryId, announceStatus]
  )

  // Helper function to get background and text color classes based on performanceLabel
  const getAumChangeStyleClasses = (performanceLabel?: string) => {
    switch (performanceLabel) {
      case 'GREAT':
        return {
          bg: 'bg-status-in-form',
          text: 'text-status-text-in-form',
          textSecondary: 'text-status-text-in-form'
        }
      case 'GOOD':
        return {
          bg: 'bg-status-on-track',
          text: 'text-status-text-on-track',
          textSecondary: 'text-status-text-on-track'
        }
      case 'AVERAGE':
        return {
          bg: 'bg-status-off-track',
          text: 'text-status-text-off-track',
          textSecondary: 'text-status-text-off-track'
        }
      default:
        return {
          bg: 'bg-[#F6F9F9]',
          text: 'text-black',
          textSecondary: 'text-[#6A6E6E]'
        }
    }
  }

  // Helper function to get background color and text color for fund age and AUM columns based on performanceLabel
  const getFundAgeAndAumBackgroundColor = (
    performanceLabel?: string
  ): { backgroundColor: string; textColor: string } => {
    switch (performanceLabel) {
      case 'GREAT':
        return { backgroundColor: 'rgba(191, 223, 255, 0.80)', textColor: '#095EB2' }
      case 'GOOD':
        return { backgroundColor: 'rgba(191, 223, 255, 0.50)', textColor: '#095EB2' }
      case 'AVERAGE':
        return { backgroundColor: 'rgba(230, 242, 255, 0.60)', textColor: '#095EB2' }
      default:
        return { backgroundColor: '#F6F9F9', textColor: '#000000' }
    }
  }

  // Helper function to format AUM change value with crores formatting
  const formatAumChangeValue = (value: number | null | undefined) => {
    if (value == null) {
      return {
        display: '-',
        isPositive: true,
        sign: '',
        croresValue: 0
      }
    }

    // Convert to crores (assuming AUM value is in rupees)
    const ONE_CRORE = 10000000
    const valueInCrores = Math.abs(value) / ONE_CRORE
    const isPositive = value >= 0
    const sign = isPositive ? '+' : '-'
    const croresValue = parseFloat(valueInCrores.toFixed(1))

    return {
      display: `${sign} ${formatNumberWithoutCurrency(croresValue)}`,
      isPositive,
      sign,
      croresValue
    }
  }

  // Helper function to build all column definitions
  const buildAllColumns = (): Record<string, ColumnDef<FundsPowerResponse>> => {
    const mfNameCardGroup: ColumnDef<FundsPowerResponse> = {
      id: 'mfNameCardGroup',
      header: () => (
        <div className='font-matter font-medium text-xs text-[#232323] px-4 text-left leading-[1.5] pt-3 flex items-center justify-between'>
          <span>{isLoading ? 'Loading...' : fundsCount ? `${fundsCount} Funds` : '0 Funds'}</span>
          {/* <button
            onClick={(e) => {
              e.stopPropagation()
              lightImpactVibrate()
              setShowFilterBottomSheet(true)
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName: 'PeerAnalyser',
                  buttonLabel: 'Open Column Reorder',
                  eventId: selectedCategoryId
                }
              })
            }}
            className='ml-2 p-1.5 rounded-lg hover:bg-[#F6F9F9] transition-colors'
            aria-label='Reorder columns'
            title='Reorder columns'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 20 20'
              fill='none'
              stroke='#6a6e6e'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M3 6h14M3 12h14M3 18h14' />
            </svg>
          </button> */}
        </div>
      ),
      columns: [
        {
          id: 'mfNameCard',
          header: () => (
            <div className='flex flex-col gap-3 items-start justify-start px-4 w-full'>
              {/* Power select_B11/B12: native button + Excel hint for category about opener (e.g. Large Cap) */}
              <button
                type='button'
                aria-description='double tap to activate'
                onClick={() => {
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      ScreenName: 'PeerAnalyser',
                      buttonLabel:
                        getCategoryLabel(selectedCategoryId) ||
                        associatedColours[selectedCategoryId]?.label ||
                        selectedCategoryId,
                      eventId: selectedCategoryId
                    }
                  })
                  lightImpactVibrate()
                  openBottomSheet(
                    <CategoryAboutBottomSheet categoryId={selectedCategoryId} onDismiss={closeBottomSheet} />,
                    closeBottomSheet,
                    { drawerTitleId: CATEGORY_ABOUT_HEADING_ID }
                  )
                }}
                className='text-left font-normal font-matter text-[11px] text-[#6a6e6e] leading-[1.4] underline w-full mb-[10px] border-none bg-transparent p-0 cursor-pointer'
              >
                {getCategoryLabel(selectedCategoryId) ||
                  associatedColours[selectedCategoryId]?.label ||
                  selectedCategoryId}
              </button>
            </div>
          ),
          cell: (info: any) => {
            const row = info.row.original
            return (
              <div className='flex flex-col'>
                <MFNameCard
                  title={row.name}
                  selected={false}
                  isUsersFund={row.isUsersFund}
                  throughFamilyAccess={throughFamilyAccess}
                />
              </div>
            )
          },
          enableSorting: false,
          enablePinning: true,
          size: mfnameColumnWidth,
          minSize: 132,
          maxSize: 132
        }
      ]
    }

    const gap0: ColumnDef<FundsPowerResponse> = {
      id: 'gap0',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 12,
      minSize: 12,
      maxSize: 12
    }

    const powerRankColumn: ColumnDef<FundsPowerResponse> = {
      id: 'powerRankColumn',
      header: () => (
        <div className='text-center font-matter font-medium text-xs text-[#232323] leading-[1.5] pt-3'>
          Rank & Performance
        </div>
      ),
      columns: [
        {
          id: 'performance',
          header: ({ column }) => (
            <SortHeader
              label='Performance'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Performance', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            return <Score value={row.powerRank} status={row.powerPerformance as any} />
          },
          enableSorting:
            data?.isUserElite ||
            data?.isSubscribedToPeerAnalyser ||
            data?.fundsPowerResponseList[0]?.powerPerformance !== 'PERFORMANCE_LOCKED',
          sortUndefined: 'last',
          filterFn: (row, _columnId, filterValue) => {
            if (!filterValue) return true
            return row.original.powerPerformance === filterValue
          },
          size: 160,
          minSize: 120,
          maxSize: 200,
          accessorFn: (row) => {
            const metric = row.powerRank
            const performance = row.powerPerformance

            if (typeof metric === 'number' && metric !== null && metric !== undefined) {
              return metric
            }

            const performanceOrder = {
              IN_FORM: 4,
              ON_TRACK: 3,
              OFF_TRACK: 2,
              OUT_OF_FORM: 1
            }

            if (performanceOrder[performance as keyof typeof performanceOrder] && metric === null) {
              return undefined
            }
            return performanceOrder[performance as keyof typeof performanceOrder] || undefined
          },
          footer: ({ column }) => {
            const hasInFormFunds = data?.fundsPowerResponseList?.some(
              (fund: FundsPowerResponse) => fund.powerPerformance === 'IN_FORM'
            )

            if (!hasInFormFunds) {
              return null
            }

            return (
              <div className={`relative w-[130px] outline-none mx-auto`}>
                <button
                  type='button'
                  aria-label={showInFormOnly ? 'View all funds' : 'View In-form'}
                  style={{ top: `${getBottomPadding() ? -1 * (getBottomPadding() + 100) : -100}px` }}
                  className={`border-1 border-grey80 w-full rounded-xl text-white bg-black px-3 py-2 absolute left-1/2 top-[-100px] -translate-x-1/2 -translate-y-1/2 focus:outline-none text-[13px]`}
                  onClick={() => {
                    lightImpactVibrate()
                    trackEvent({
                      eventName: EventNames.ButtonTap,
                      payload: {
                        ScreenName: 'PeerAnalyser',
                        buttonLabel: !showInFormOnly ? 'View In-form' : 'View all funds',
                        eventId: selectedCategoryId
                      }
                    })
                    setIsFiltering(true)
                    setShowInFormOnly(!showInFormOnly)
                    if (!showInFormOnly) {
                      column.setFilterValue('IN_FORM')
                      announceStatus('Showing in-form funds only')
                    } else {
                      column.setFilterValue('')
                      announceStatus('Showing all funds')
                    }
                    setTimeout(() => setIsFiltering(false), 100)
                  }}
                >
                  <div className='flex items-center gap-2' aria-hidden='true'>
                    <span className='mt-[3px]'>
                      {showInFormOnly ? (
                        <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'>
                          <path
                            fillRule='evenodd'
                            clipRule='evenodd'
                            d='M1.14286 1.875C1.14286 1.66789 1.30276 1.5 1.5 1.5H10.6429C10.8401 1.5 11 1.66789 11 1.875V3C11 3.2071 10.8401 3.375 10.6429 3.375H1.5C1.30276 3.375 1.14286 3.2071 1.14286 3V1.875ZM1 5.4375C1 5.23039 1.1599 5.0625 1.35714 5.0625H10.5C10.6973 5.0625 10.8571 5.23039 10.8571 5.4375V6.5625C10.8571 6.76961 10.6973 6.9375 10.5 6.9375H1.35714C1.1599 6.9375 1 6.76961 1 6.5625V5.4375ZM1.35714 8.625C1.1599 8.625 1 8.79289 1 9V10.125C1 10.3321 1.1599 10.5 1.35714 10.5H7.5C7.69725 10.5 7.85714 10.3321 7.85714 10.125V9C7.85714 8.79289 7.69725 8.625 7.5 8.625H1.35714Z'
                            fill='white'
                          />
                        </svg>
                      ) : (
                        <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'>
                          <path
                            d='M2 5.97656L6 2.47656L10 5.97656'
                            stroke='white'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                          <path
                            d='M2 10.4766L6 6.97656L10 10.4766'
                            stroke='white'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </span>
                    <span className='text-white'>{showInFormOnly ? 'View all funds' : 'View In-form'}</span>
                  </div>
                </button>
              </div>
            )
          }
        }
      ]
    }

    const gap1: ColumnDef<FundsPowerResponse> = {
      id: 'gap1',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 12,
      minSize: 12,
      maxSize: 12
    }

    const eliteScores: ColumnDef<FundsPowerResponse> = {
      id: 'eliteScores',
      header: () => (
        <div className='text-center font-matter font-medium text-xs text-[#232323] leading-[1.5] pt-3'>
          Elite Scores
        </div>
      ),
      size: 156,
      minSize: 156,
      maxSize: 156,
      enableResizing: false,
      columns: [
        {
          id: 'eliteConsistency',
          header: ({ column }) => (
            <SortHeader
              label='Consistency&nbsp;'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Consistency', column)}
            />
          ),
          accessorFn: (row) => {
            const metric = row.consistencyScore
            return typeof metric === 'number' ? metric : undefined
          },
          cell: (info) => {
            const row = info.row.original
            return (
              <EliteScores
                performanceLabel={row.consistencyPerformanceTag}
                value={row.consistencyScore}
                status={row.powerPerformance}
              />
            )
          },
          sortUndefined: 'last',
          enableSorting: data?.isUserElite || data?.isSubscribedToPeerAnalyser,
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'eliteVolatility',
          header: ({ column }) => (
            <SortHeader
              label='Volatility'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Volatility', column)}
            />
          ),
          accessorFn: (row) => {
            const metric = row.volatilityScore
            return typeof metric === 'number' ? metric : undefined
          },
          cell: (info) => {
            const row = info.row.original
            return (
              <EliteScores
                performanceLabel={row.volatilityPerformanceTag}
                value={row.volatilityScore}
                status={row.powerPerformance}
              />
            )
          },
          sortUndefined: 'last',
          enableSorting: data?.isUserElite || data?.isSubscribedToPeerAnalyser,
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'eliteRecency',
          header: ({ column }) => (
            <SortHeader
              label='Recency'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Recency', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            return (
              <EliteScores
                performanceLabel={row.recencyPerformanceTag}
                value={row.recencyScore}
                status={row.powerPerformance}
              />
            )
          },
          accessorFn: (row) => {
            const metric = row.recencyScore
            return typeof metric === 'number' ? metric : undefined
          },
          sortUndefined: 'last',
          enableSorting: data?.isUserElite || data?.isSubscribedToPeerAnalyser,
          size: 52,
          minSize: 52,
          maxSize: 52,
          footer: () => !isLoading && <PeerAnalyserUnlock />
        }
      ]
    }

    const gap2: ColumnDef<FundsPowerResponse> = {
      id: 'gap2',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 11,
      minSize: 11,
      maxSize: 11
    }

    const rollingReturns: ColumnDef<FundsPowerResponse> = {
      id: 'rollingReturns',
      header: () => (
        <div className='text-center font-matter font-medium text-xs text-[#232323] leading-[1.5] pt-3'>
          Rolling Returns
        </div>
      ),
      columns: [
        {
          id: 'rollingReturns1Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='1Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Rolling Returns 1Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
            )
            return (
              <Returns
                performanceLabel={metric?.performanceLabel}
                value={metric?.value.amount}
                status={row.powerPerformance as any}
                rank={metric?.rank}
                showRank={showRollingReturnsRank}
                powerPerformance={row.powerPerformance}
              />
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'rollingReturns3Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='3Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Rolling Returns 3Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
            )
            return (
              <Returns
                performanceLabel={metric?.performanceLabel}
                value={metric?.value.amount}
                status={row.powerPerformance as any}
                rank={metric?.rank}
                showRank={showRollingReturnsRank}
                powerPerformance={row.powerPerformance}
              />
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'rollingReturns5Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='5Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Rolling Returns 5Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
            )
            return (
              <Returns
                performanceLabel={metric?.performanceLabel}
                value={metric?.value.amount}
                status={row.powerPerformance as any}
                rank={metric?.rank}
                showRank={showRollingReturnsRank}
                powerPerformance={row.powerPerformance}
              />
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        }
      ]
    }

    const gap3: ColumnDef<FundsPowerResponse> = {
      id: 'gap3',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 11,
      minSize: 11,
      maxSize: 11
    }

    const trailingReturns: ColumnDef<FundsPowerResponse> = {
      id: 'trailingReturns',
      header: () => (
        <div className='text-center font-matter font-medium text-xs text-[#232323] leading-[1.5] pt-3'>
          Trailing Returns
        </div>
      ),
      columns: [
        {
          id: 'trailingReturns1Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='1Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Trailing Returns 1Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
            )
            return (
              <Returns
                performanceLabel={metric?.performanceLabel}
                value={metric?.value.amount}
                status={row.powerPerformance as any}
                rank={metric?.rank}
                showRank={showTrailingReturnsRank}
                powerPerformance={row.powerPerformance}
              />
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52,
          footer: () => null
        },
        {
          id: 'trailingReturns3Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='3Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Trailing Returns 3Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
            )
            return (
              <Returns
                performanceLabel={metric?.performanceLabel}
                value={metric?.value.amount}
                status={row.powerPerformance as any}
                rank={metric?.rank}
                showRank={showTrailingReturnsRank}
                powerPerformance={row.powerPerformance}
              />
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'trailingReturns5Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='5Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Trailing Returns 5Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
            )
            return (
              <Returns
                performanceLabel={metric?.performanceLabel}
                value={metric?.value.amount}
                status={row.powerPerformance as any}
                rank={metric?.rank}
                showRank={showTrailingReturnsRank}
                powerPerformance={row.powerPerformance}
              />
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        }
      ]
    }

    const gap4: ColumnDef<FundsPowerResponse> = {
      id: 'gap4',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 11,
      minSize: 11,
      maxSize: 11
    }

    const aumChange: ColumnDef<FundsPowerResponse> = {
      id: 'aumChange',
      header: () => (
        <div className='text-center font-matter font-medium text-xs text-[#232323] leading-[1.5] pt-3'>AUM Change</div>
      ),
      columns: [
        {
          id: 'aumChange1M',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 1
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='1M'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'AUM Change 1M', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 1
            )
            const value = metric?.value.amount
            const formatted = formatAumChangeValue(value)
            const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

            if (value == null) {
              return (
                <div className='flex flex-row'>
                  <div className='w-[50px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
                    <span className='text-xs text-black font-medium'>-</span>
                  </div>
                </div>
              )
            }

            return (
              <div className='flex flex-row'>
                <div
                  className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
                  aria-label={`AUM Change 1M: ${formatted.display} Crores`}
                >
                  <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>{formatted.display}</span>
                  <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>Crores</span>
                </div>
              </div>
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'aumChange6M',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 6
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='6M'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'AUM Change 6M', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 6
            )
            const value = metric?.value.amount
            const formatted = formatAumChangeValue(value)
            const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

            if (value == null) {
              return (
                <div className='flex flex-row'>
                  <div className='w-[50px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
                    <span className='text-xs text-black font-medium'>-</span>
                  </div>
                </div>
              )
            }

            return (
              <div className='flex flex-row'>
                <div
                  className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
                  aria-label={`AUM Change 6M: ${formatted.display} Crores`}
                >
                  <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>{formatted.display}</span>
                  <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>Crores</span>
                </div>
              </div>
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'aumChange1Y',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'YEAR' && m.tenure.value === 1
            )
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='1Y'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'AUM Change 1Y', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find(
              (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'YEAR' && m.tenure.value === 1
            )
            const value = metric?.value.amount
            const formatted = formatAumChangeValue(value)
            const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

            if (value == null) {
              return (
                <div className='flex flex-row'>
                  <div className='w-[50px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
                    <span className='text-xs text-black font-medium'>-</span>
                  </div>
                </div>
              )
            }

            return (
              <div className='flex flex-row'>
                <div
                  className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
                  aria-label={`AUM Change 1Y: ${formatted.display} Crores`}
                >
                  <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>{formatted.display}</span>
                  <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>Crores</span>
                </div>
              </div>
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        }
      ]
    }

    const gap5: ColumnDef<FundsPowerResponse> = {
      id: 'gap5',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 12,
      minSize: 12,
      maxSize: 12
    }

    const fundAgeAndAum: ColumnDef<FundsPowerResponse> = {
      id: 'fundAgeAndAum',
      header: () => (
        <div className='text-center font-matter font-medium text-[11px] text-[#232323] leading-[1.5] pt-3'>
          Fund Age and AUM
        </div>
      ),
      columns: [
        {
          id: 'age',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find((m) => m.metricsType === 'AGE')
            return metric?.tenure.value
          },
          header: ({ column }) => (
            <SortHeader
              label='Age'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'Age', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find((m) => m.metricsType === 'AGE')
            const ageValue = metric?.tenure.value
            const agePeriod = metric?.tenure.period
            const { backgroundColor, textColor } = getFundAgeAndAumBackgroundColor(metric?.performanceLabel)

            if (ageValue == null) {
              return (
                <div className='flex flex-row'>
                  <div
                    className='w-[50px] h-16 relative rounded-sm flex-shrink-0 flex items-center justify-center'
                    style={{ backgroundColor }}
                  >
                    <span className='text-xs font-medium' style={{ color: textColor }}>
                      -
                    </span>
                  </div>
                </div>
              )
            }

            const periodLabel =
              ageValue === 0 && agePeriod === 'YEAR'
                ? '<1yr'
                : agePeriod === 'YEAR'
                  ? 'yr'
                  : agePeriod === 'MONTH'
                    ? 'Mths'
                    : agePeriod === 'DAY'
                      ? 'Days'
                      : ''

            return (
              <div className='flex flex-row'>
                <div
                  className='w-[50px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center'
                  style={{ backgroundColor }}
                >
                  <span className='text-xs font-medium' style={{ color: textColor }}>
                    {ageValue > 0 ? ageValue : ''}
                    {periodLabel}
                  </span>
                  {/* <span className='text-[10px] font-normal' style={{ color: '#095EB2' }}></span> */}
                </div>
              </div>
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        },
        {
          id: 'aum',
          accessorFn: (row) => {
            const metric = row.financialMetrics.find((m) => m.metricsType === 'AUM')
            return metric?.value.amount
          },
          header: ({ column }) => (
            <SortHeader
              label='AUM'
              sortState={column.getIsSorted() === false ? 'inactive' : column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
              onClick={handleHeaderClick(column.getToggleSortingHandler(), 'AUM', column)}
            />
          ),
          cell: (info) => {
            const row = info.row.original
            const metric = row.financialMetrics.find((m) => m.metricsType === 'AUM')
            const aumValue = metric?.value.amount
            const { backgroundColor, textColor } = getFundAgeAndAumBackgroundColor(metric?.performanceLabel)

            if (aumValue == null) {
              return (
                <div className='flex flex-row'>
                  <div
                    className='w-[64px] h-16 relative rounded-sm flex-shrink-0 flex items-center justify-center'
                    style={{ backgroundColor }}
                  >
                    <span className='text-xs font-medium' style={{ color: textColor }}>
                      -
                    </span>
                  </div>
                </div>
              )
            }

            // Convert to crores (assuming AUM value is in rupees)
            const ONE_CRORE = 10000000
            const valueInCrores = Math.abs(aumValue) / ONE_CRORE
            const croresValue = parseFloat(valueInCrores.toFixed(1))

            return (
              <div className='flex flex-row'>
                <div
                  className='w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center'
                  style={{ backgroundColor }}
                >
                  <span className='text-xs font-medium' style={{ color: textColor }}>
                    {formatNumberWithoutCurrency(croresValue)}
                  </span>
                  <span className='text-[10px] font-normal' style={{ color: textColor }}>
                    Crores
                  </span>
                </div>
              </div>
            )
          },
          enableSorting: true,
          sortUndefined: 'last',
          size: 52,
          minSize: 52,
          maxSize: 52
        }
      ]
    }

    const gap6: ColumnDef<FundsPowerResponse> = {
      id: 'gap6',
      header: () => '',
      cell: () => null,
      enableSorting: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableResizing: false,
      size: 12,
      minSize: 12,
      maxSize: 12
    }

    return {
      mfNameCardGroup,
      gap0,
      powerRankColumn,
      gap1,
      eliteScores,
      gap2,
      rollingReturns,
      gap3,
      trailingReturns,
      gap4,
      aumChange,
      gap5,
      fundAgeAndAum,
      gap6
    }
  }

  // Column metadata for the reorder modal (excluding gap columns)
  const columnMetadata: ColumnItem[] = [
    { id: 'powerRankColumn', label: 'Rank & Performance' },
    { id: 'eliteScores', label: 'Elite Scores' },
    { id: 'rollingReturns', label: 'Rolling Returns' },
    { id: 'trailingReturns', label: 'Trailing Returns' },
    { id: 'aumChange', label: 'AUM Change' },
    { id: 'fundAgeAndAum', label: 'Fund Age and AUM' }
  ]

  const handleColumnReorder = (newOrder: string[]) => {
    // Merge reordered columns with gap columns and normalize spacing, ensuring boundary gaps
    const mergedOrder = ensureBoundaryGaps(normalizeGapColumns(mergeWithGapColumns(newOrder)))
    setColumnOrder(mergedOrder)
    // Note: Column order is now managed by backend only - no localStorage storage
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'PeerAnalyser',
        buttonLabel: 'Reorder Columns',
        eventId: selectedCategoryId
      }
    })
  }

  const columns: ColumnDef<FundsPowerResponse>[] = useMemo(() => {
    const allColumns = buildAllColumns()
    const orderedColumns: ColumnDef<FundsPowerResponse>[] = [allColumns.mfNameCardGroup]

    // Add columns in the specified order
    columnOrder.forEach((columnId) => {
      const columnKey = columnId as keyof typeof allColumns
      if (allColumns[columnKey]) {
        orderedColumns.push(allColumns[columnKey])
      }
    })

    return orderedColumns
  }, [
    columnOrder,
    showRollingReturnsRank,
    showTrailingReturnsRank,
    showInFormOnly,
    fundsCount,
    isLoading,
    selectedCategoryId,
    mfnameColumnWidth,
    data,
    handleHeaderClick,
    announceStatus
  ])

  // Calculate sticky top offsets for pinned rows
  const pinnedRowHeights = useRef<{ [key: string]: number }>({})
  const [pinnedOffsets, setPinnedOffsets] = useState<{ [key: string]: number }>({})

  // Only unpinned rows are passed to the table for sorting/filtering
  const unpinnedData = useMemo(
    () => data?.fundsPowerResponseList?.filter((row: FundsPowerResponse) => !pinnedRowIds.includes(row.isin)),
    [data, pinnedRowIds]
  )

  // Update funds count based on filtered data
  useMemo(() => {
    const totalCount = data?.fundsPowerResponseList?.length || 0
    setFundsCount(totalCount)
  }, [data?.fundsPowerResponseList])

  const table = useReactTable({
    data: unpinnedData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updater) => {
      setIsSorting(true)
      setSorting(updater)
      // Reset sorting flag after a short delay to allow the sort to complete
      setTimeout(() => setIsSorting(false), 100)
    },
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnPinningChange: setColumnPinning,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnPinning,
      rowSelection
    },
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    getRowId: (row) => row.isin // Use 'name' as unique row id
  })

  const handleRowSelect = (rowId: string) => {
    lightImpactVibrate()

    const isPinned = pinnedRowIds.includes(rowId)
    const action = isPinned ? 'UNBOOKMARK' : 'BOOKMARK'

    // Track bookmark/unbookmark event
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'PeerAnalyser',
        buttonLabel: action === 'BOOKMARK' ? 'Bookmark Fund' : 'Unbookmark Fund',
        eventId: selectedCategoryId,
        fundIsin: rowId // Using fundName to pass isin
      }
    })

    bookmarkFund({
      isin: rowId,
      fundCategory: selectedCategoryId,
      screenSource: 'PEER_ANALYSER',
      action
    })
  }

  useLayoutEffect(() => {
    // Calculate heights and offsets for pinned rows
    let offset = headerRef.current ? headerRef.current.offsetHeight : 0
    const newOffsets: { [key: string]: number } = {}
    pinnedRowIds.forEach((rowId) => {
      const rowElement = rowRefs.current[rowId]
      if (rowElement) {
        newOffsets[rowId] = offset
        pinnedRowHeights.current[rowId] = rowElement.offsetHeight
        offset += rowElement.offsetHeight
      }
    })
    setPinnedOffsets(newOffsets)
  }, [pinnedRowIds, showRankInNameCard])

  // Calculate empty rows needed to fill the screen
  useLayoutEffect(() => {
    const calculateEmptyRows = () => {
      if (!tableContainerRef.current || !headerRef.current) return

      const ROW_HEIGHT = 64 // min-h-[64px]
      const FOOTER_HEIGHT = 48 // Approximate footer height

      const containerHeight = maxHeight
      const headerHeight = headerRef.current.offsetHeight

      // Get the actual number of visible rows
      const visibleRowCount = table.getRowModel().rows.length

      // Calculate content height
      const contentHeight = headerHeight + visibleRowCount * ROW_HEIGHT + FOOTER_HEIGHT

      // Calculate remaining space
      const remainingSpace = containerHeight - contentHeight

      // Calculate how many empty rows we need (add 1 extra)
      if (remainingSpace > 0) {
        const emptyRows = Math.ceil(remainingSpace / ROW_HEIGHT) + 1
        setEmptyRowsNeeded(emptyRows)
      } else {
        setEmptyRowsNeeded(2) // Always add at least 1 extra row
      }
    }

    calculateEmptyRows()
  }, [maxHeight, table, showInFormOnly])

  // Helper function to flatten grouped columns for pinned row rendering
  const getFlattenedColumns = () => {
    const flattened: any[] = []
    columns.forEach((col) => {
      if ((col as any).columns) {
        // This is a grouped column, add all its sub-columns
        flattened.push(...(col as any).columns)
      } else {
        // This is a regular column
        flattened.push(col)
      }
    })
    return flattened
  }

  // For pinned rows, filter mockData by pinnedRowIds and current filters
  // We'll use the same filter logic as TanStack Table for consistency
  // For now, we use the columnFilters state to filter pinned rows
  const filterPinnedRow = (row: FundsPowerResponse) => {
    // For each filter, check if the row matches
    for (const filter of columnFilters) {
      const { id, value } = filter
      if (!value) continue
      // Handle nested keys (e.g., 'eliteScores.returns')
      const keys = id.split('.')
      let rowValue: any = row
      for (const key of keys) {
        rowValue = rowValue?.[key]
      }
      if (typeof rowValue === 'string' || typeof rowValue === 'number') {
        if (!rowValue.toString().toLowerCase().includes(value.toString().toLowerCase())) {
          return false
        }
      } else {
        // If value is not string/number, skip filter
        continue
      }
    }
    return true
  }

  const motionTransition = {
    type: 'tween',
    duration: allowAnimation && !isSorting && !isFiltering ? 0.25 : 0,
    ease: 'easeInOut'
  }

  const pinnedRows = pinnedRowIds
    .map((rowId) => (data?.fundsPowerResponseList ?? []).find((row: FundsPowerResponse) => row.isin === rowId))
    .filter((row): row is FundsPowerResponse => !!row && filterPinnedRow(row as any))
    .map((row) => {
      const rowTopOffset = pinnedOffsets[row.isin] || 0
      return (
        <motion.tr
          key={row.isin + '-pinned'}
          layout='position'
          layoutId={row.isin}
          transition={motionTransition}
          className={'sticky z-50 p-[0.5px]'}
          style={{ top: rowTopOffset, background: '#fff' }}
          ref={(el) => {
            if (el) rowRefs.current[row.isin] = el
          }}
        >
          {/* Render MFNameCard in the first column */}
          <td className={'sticky left-0 bg-white border-b border-r border-[#EBF2F2] z-10 p-[0.5px]'}>
            <div className='flex flex-col'>
              <MFNameCard
                title={row.name}
                selected={pinnedRowIds.includes(row.isin)}
                isUsersFund={row.isUsersFund}
                onBookmarkClick={() => handleRowSelect(row.isin)}
                isin={row.isin}
                throughFamilyAccess={throughFamilyAccess}
              />
            </div>
          </td>
          {/* Render the rest of the cells, skipping the first column */}
          {getFlattenedColumns()
            .slice(1)
            .map((col) => {
              if (col.id === 'performance') {
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Score value={row.powerRank} status={row.powerPerformance as any} />
                  </td>
                )
              }
              if (col.id === 'eliteRecency') {
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <EliteScores
                      performanceLabel={row.recencyPerformanceTag}
                      value={row.recencyScore}
                      status={row.powerPerformance as any}
                    />
                  </td>
                )
              }
              if (col.id === 'eliteVolatility') {
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <EliteScores
                      performanceLabel={row.volatilityPerformanceTag}
                      value={row.volatilityScore}
                      status={row.powerPerformance as any}
                    />
                  </td>
                )
              }
              if (col.id === 'eliteConsistency') {
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <EliteScores
                      performanceLabel={row.consistencyPerformanceTag}
                      value={row.consistencyScore}
                      status={row.powerPerformance as any}
                    />
                  </td>
                )
              }
              if (col.id === 'rollingReturns1Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
                )
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Returns
                      performanceLabel={metric?.performanceLabel}
                      value={metric?.value.amount != null ? Number(metric.value.amount.toFixed(1)) : 0}
                      status={row.powerPerformance as any}
                      rank={metric?.rank}
                      showRank={showRollingReturnsRank}
                      powerPerformance={row.powerPerformance}
                    />
                  </td>
                )
              }
              if (col.id === 'rollingReturns3Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
                )
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Returns
                      performanceLabel={metric?.performanceLabel}
                      value={metric?.value.amount != null ? Number(metric.value.amount.toFixed(1)) : 0}
                      status={row.powerPerformance as any}
                      rank={metric?.rank}
                      showRank={showRollingReturnsRank}
                      powerPerformance={row.powerPerformance}
                    />
                  </td>
                )
              }
              if (col.id === 'rollingReturns5Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
                )
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Returns
                      performanceLabel={metric?.performanceLabel}
                      value={metric?.value.amount != null ? Number(metric.value.amount.toFixed(1)) : 0}
                      status={row.powerPerformance as any}
                      rank={metric?.rank}
                      showRank={showRollingReturnsRank}
                      powerPerformance={row.powerPerformance}
                    />
                  </td>
                )
              }
              if (col.id === 'trailingReturns1Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
                )
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Returns
                      performanceLabel={metric?.performanceLabel}
                      value={metric?.value.amount != null ? Number(metric.value.amount.toFixed(1)) : 0}
                      status={row.powerPerformance as any}
                      rank={metric?.rank}
                      showRank={showTrailingReturnsRank}
                      powerPerformance={row.powerPerformance}
                    />
                  </td>
                )
              }
              if (col.id === 'trailingReturns3Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
                )
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Returns
                      performanceLabel={metric?.performanceLabel}
                      value={metric?.value.amount != null ? Number(metric.value.amount.toFixed(1)) : 0}
                      status={row.powerPerformance as any}
                      rank={metric?.rank}
                      showRank={showTrailingReturnsRank}
                      powerPerformance={row.powerPerformance}
                    />
                  </td>
                )
              }
              if (col.id === 'trailingReturns5Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
                )
                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <Returns
                      performanceLabel={metric?.performanceLabel}
                      value={metric?.value.amount != null ? Number(metric.value.amount.toFixed(1)) : 0}
                      status={row.powerPerformance as any}
                      rank={metric?.rank}
                      showRank={showTrailingReturnsRank}
                      powerPerformance={row.powerPerformance}
                    />
                  </td>
                )
              }
              if (col.id === 'aumChange1M') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 1
                )
                const value = metric?.value.amount
                const formatted = formatAumChangeValue(value)
                const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

                if (value == null) {
                  return (
                    <td key={col.id} className='p-[0.5px]'>
                      <div className='flex flex-row'>
                        <div className='w-[64px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
                          <span className='text-xs text-black font-medium'>-</span>
                        </div>
                      </div>
                    </td>
                  )
                }

                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <div className='flex flex-row'>
                      <div
                        className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
                        aria-label={`AUM Change 1M: ${formatted.display} Crores`}
                      >
                        <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>
                          {formatted.display}
                        </span>
                        <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>
                          Crores
                        </span>
                      </div>
                    </div>
                  </td>
                )
              }
              if (col.id === 'aumChange6M') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 6
                )
                const value = metric?.value.amount
                const formatted = formatAumChangeValue(value)
                const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

                if (value == null) {
                  return (
                    <td key={col.id} className='p-[0.5px]'>
                      <div className='flex flex-row'>
                        <div className='w-[64px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
                          <span className='text-xs text-black font-medium'>-</span>
                        </div>
                      </div>
                    </td>
                  )
                }

                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <div className='flex flex-row'>
                      <div
                        className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
                        aria-label={`AUM Change 6M: ${formatted.display} Crores`}
                      >
                        <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>
                          {formatted.display}
                        </span>
                        <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>
                          Crores
                        </span>
                      </div>
                    </div>
                  </td>
                )
              }
              if (col.id === 'aumChange1Y') {
                const metric = row.financialMetrics.find(
                  (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'YEAR' && m.tenure.value === 1
                )
                const value = metric?.value.amount
                const formatted = formatAumChangeValue(value)
                const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

                if (value == null) {
                  return (
                    <td key={col.id} className='p-[0.5px]'>
                      <div className='flex flex-row'>
                        <div className='w-[64px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
                          <span className='text-xs text-black font-medium'>-</span>
                        </div>
                      </div>
                    </td>
                  )
                }

                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <div className='flex flex-row'>
                      <div
                        className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
                        aria-label={`AUM Change 1Y: ${formatted.display} Crores`}
                      >
                        <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>
                          {formatted.display}
                        </span>
                        <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>
                          Crores
                        </span>
                      </div>
                    </div>
                  </td>
                )
              }
              if (col.id === 'age') {
                const metric = row.financialMetrics.find((m) => m.metricsType === 'AGE')
                const ageValue = metric?.tenure.value
                const agePeriod = metric?.tenure.period
                const { backgroundColor, textColor } = getFundAgeAndAumBackgroundColor(metric?.performanceLabel)

                if (ageValue == null) {
                  return (
                    <td key={col.id} className='p-[0.5px]'>
                      <div className='flex flex-row'>
                        <div
                          className='w-[50px] h-16 relative rounded-sm flex-shrink-0 flex items-center justify-center'
                          style={{ backgroundColor }}
                        >
                          <span className='text-xs font-medium' style={{ color: textColor }}>
                            -
                          </span>
                        </div>
                      </div>
                    </td>
                  )
                }

                const periodLabel =
                  ageValue === 0 && agePeriod === 'YEAR'
                    ? '<1yr'
                    : agePeriod === 'YEAR'
                      ? 'yr'
                      : agePeriod === 'MONTH'
                        ? 'Mths'
                        : agePeriod === 'DAY'
                          ? 'Days'
                          : ''

                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <div className='flex flex-row'>
                      <div
                        className='w-[50px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center'
                        style={{ backgroundColor }}
                      >
                        <span className='text-xs font-medium' style={{ color: textColor }}>
                          {ageValue > 0 ? ageValue : ''}
                          {periodLabel}
                        </span>
                        {/* <span className='text-[10px] font-normal' style={{ color: '#095EB2' }}> */}

                        {/* </span> */}
                      </div>
                    </div>
                  </td>
                )
              }
              if (col.id === 'aum') {
                const metric = row.financialMetrics.find((m) => m.metricsType === 'AUM')
                const aumValue = metric?.value.amount
                const { backgroundColor, textColor } = getFundAgeAndAumBackgroundColor(metric?.performanceLabel)

                if (aumValue == null) {
                  return (
                    <td key={col.id} className='p-[0.5px]'>
                      <div className='flex flex-row'>
                        <div
                          className='w-[64px] h-16 relative rounded-sm flex-shrink-0 flex items-center justify-center'
                          style={{ backgroundColor }}
                        >
                          <span className='text-xs font-medium' style={{ color: textColor }}>
                            -
                          </span>
                        </div>
                      </div>
                    </td>
                  )
                }

                // Convert to crores (assuming AUM value is in rupees)
                const ONE_CRORE = 10000000
                const valueInCrores = Math.abs(aumValue) / ONE_CRORE
                const croresValue = parseFloat(valueInCrores.toFixed(1))

                return (
                  <td key={col.id} className='p-[0.5px]'>
                    <div className='flex flex-row'>
                      <div
                        className='w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center'
                        style={{ backgroundColor }}
                      >
                        <span className='text-xs font-medium' style={{ color: textColor }}>
                          {formatNumberWithoutCurrency(croresValue)}
                        </span>
                        <span className='text-[10px] font-normal' style={{ color: textColor }}>
                          Crores
                        </span>
                      </div>
                    </div>
                  </td>
                )
              }
              // For gap columns or unknown, render empty cell
              return (
                <td
                  key={col.id}
                  className='p-[0.5px]'
                  aria-hidden={col.id.startsWith('gap') ? true : undefined}
                ></td>
              )
            })}
        </motion.tr>
      )
    })

  // Helper function to render cells directly (like pinned rows)
  const renderCell = (row: FundsPowerResponse, colId: string) => {
    if (colId === 'performance') {
      return <Score value={row.powerRank} status={row.powerPerformance as any} />
    }
    if (colId === 'eliteRecency') {
      return (
        <EliteScores
          value={row.recencyScore}
          status={row.powerPerformance as any}
          performanceLabel={row.recencyPerformanceTag}
        />
      )
    }
    if (colId === 'eliteVolatility') {
      return (
        <EliteScores
          value={row.volatilityScore}
          status={row.powerPerformance as any}
          performanceLabel={row.volatilityPerformanceTag}
        />
      )
    }
    if (colId === 'eliteConsistency') {
      return (
        <EliteScores
          value={row.consistencyScore}
          status={row.powerPerformance as any}
          performanceLabel={row.consistencyPerformanceTag}
        />
      )
    }
    if (colId === 'rollingReturns1Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
      )
      return (
        <Returns
          performanceLabel={metric?.performanceLabel}
          value={metric?.value.amount}
          status={row.powerPerformance as any}
          rank={metric?.rank}
          showRank={showRollingReturnsRank}
          powerPerformance={row.powerPerformance}
        />
      )
    }
    if (colId === 'rollingReturns3Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
      )
      return (
        <Returns
          performanceLabel={metric?.performanceLabel}
          value={metric?.value.amount}
          status={row.powerPerformance as any}
          rank={metric?.rank}
          showRank={showRollingReturnsRank}
          powerPerformance={row.powerPerformance}
        />
      )
    }
    if (colId === 'rollingReturns5Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'ROLLING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
      )
      return (
        <Returns
          performanceLabel={metric?.performanceLabel}
          value={metric?.value.amount}
          status={row.powerPerformance as any}
          rank={metric?.rank}
          showRank={showRollingReturnsRank}
          powerPerformance={row.powerPerformance}
        />
      )
    }
    if (colId === 'trailingReturns1Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 1 && m.tenure.period === 'YEAR'
      )
      return (
        <Returns
          performanceLabel={metric?.performanceLabel}
          value={metric?.value.amount}
          status={row.powerPerformance as any}
          rank={metric?.rank}
          showRank={showTrailingReturnsRank}
          powerPerformance={row.powerPerformance}
        />
      )
    }
    if (colId === 'trailingReturns3Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 3 && m.tenure.period === 'YEAR'
      )
      return (
        <Returns
          performanceLabel={metric?.performanceLabel}
          value={metric?.value.amount}
          status={row.powerPerformance as any}
          rank={metric?.rank}
          showRank={showTrailingReturnsRank}
          powerPerformance={row.powerPerformance}
        />
      )
    }
    if (colId === 'trailingReturns5Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'TRAILING_RETURNS' && m.tenure.value === 5 && m.tenure.period === 'YEAR'
      )
      return (
        <Returns
          performanceLabel={metric?.performanceLabel}
          value={metric?.value.amount}
          status={row.powerPerformance as any}
          rank={metric?.rank}
          showRank={showTrailingReturnsRank}
          powerPerformance={row.powerPerformance}
        />
      )
    }
    if (colId === 'aumChange1M') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 1
      )
      const value = metric?.value.amount
      const formatted = formatAumChangeValue(value)
      const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

      if (value == null) {
        return (
          <div className='flex flex-row'>
            <div className='w-[64px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
              <span className='text-xs text-black font-medium'>-</span>
            </div>
          </div>
        )
      }

      return (
        <div className='flex flex-row'>
          <div
            className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
            aria-label={`AUM Change 1M: ${formatted.display} Crores`}
          >
            <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>{formatted.display}</span>
            <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>Crores</span>
          </div>
        </div>
      )
    }
    if (colId === 'aumChange6M') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'MONTH' && m.tenure.value === 6
      )
      const value = metric?.value.amount
      const formatted = formatAumChangeValue(value)
      const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

      if (value == null) {
        return (
          <div className='flex flex-row'>
            <div className='w-[64px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
              <span className='text-xs text-black font-medium'>-</span>
            </div>
          </div>
        )
      }

      return (
        <div className='flex flex-row'>
          <div
            className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
            aria-label={`AUM Change 6M: ${formatted.display} Crores`}
          >
            <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>{formatted.display}</span>
            <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>Crores</span>
          </div>
        </div>
      )
    }
    if (colId === 'aumChange1Y') {
      const metric = row.financialMetrics.find(
        (m) => m.metricsType === 'AUM_CHANGE' && m.tenure.period === 'YEAR' && m.tenure.value === 1
      )
      const value = metric?.value.amount
      const formatted = formatAumChangeValue(value)
      const styleClasses = getAumChangeStyleClasses(metric?.performanceLabel)

      if (value == null) {
        return (
          <div className='flex flex-row'>
            <div className='w-[64px] h-16 bg-[#F6F9F9] relative rounded-sm flex-shrink-0 flex items-center justify-center'>
              <span className='text-xs text-black font-medium'>-</span>
            </div>
          </div>
        )
      }

      return (
        <div className='flex flex-row'>
          <div
            className={`w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center ${styleClasses.bg}`}
            aria-label={`AUM Change 1Y: ${formatted.display} Crores`}
          >
            <span className={`text-xs font-medium leading-tight ${styleClasses.text}`}>{formatted.display}</span>
            <span className={`text-[10px] font-normal leading-tight ${styleClasses.textSecondary}`}>Crores</span>
          </div>
        </div>
      )
    }
    if (colId === 'age') {
      const metric = row.financialMetrics.find((m) => m.metricsType === 'AGE')
      const ageValue = metric?.tenure.value
      const agePeriod = metric?.tenure.period
      const { backgroundColor, textColor } = getFundAgeAndAumBackgroundColor(metric?.performanceLabel)

      if (ageValue == null) {
        return (
          <div className='flex flex-row'>
            <div
              className='w-[50px] h-16 relative rounded-sm flex-shrink-0 flex items-center justify-center'
              style={{ backgroundColor }}
            >
              <span className='text-xs font-medium' style={{ color: textColor }}>
                -
              </span>
            </div>
          </div>
        )
      }

      const periodLabel =
        ageValue === 0 && agePeriod === 'YEAR'
          ? '<1yr'
          : agePeriod === 'YEAR'
            ? 'yr'
            : agePeriod === 'MONTH'
              ? 'Mths'
              : agePeriod === 'DAY'
                ? 'Days'
                : ''

      return (
        <div className='flex flex-row'>
          <div
            className='w-[50px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center'
            style={{ backgroundColor }}
          >
            <span className='text-xs font-medium' style={{ color: textColor }}>
              {ageValue > 0 ? ageValue : ''}
              {periodLabel}
            </span>
          </div>
        </div>
      )
    }
    if (colId === 'aum') {
      const metric = row.financialMetrics.find((m) => m.metricsType === 'AUM')
      const aumValue = metric?.value.amount
      const { backgroundColor, textColor } = getFundAgeAndAumBackgroundColor(metric?.performanceLabel)

      if (aumValue == null) {
        return (
          <div className='flex flex-row'>
            <div
              className='w-[64px] h-16 relative rounded-sm flex-shrink-0 flex items-center justify-center'
              style={{ backgroundColor }}
            >
              <span className='text-xs font-medium' style={{ color: textColor }}>
                -
              </span>
            </div>
          </div>
        )
      }

      // Convert to crores (assuming AUM value is in rupees)
      const ONE_CRORE = 10000000
      const valueInCrores = Math.abs(aumValue) / ONE_CRORE
      const croresValue = parseFloat(valueInCrores.toFixed(1))

      return (
        <div className='flex flex-row'>
          <div
            className='w-[64px] h-16 relative rounded-sm flex-shrink-0 flex flex-col items-center justify-center'
            style={{ backgroundColor }}
          >
            <span className='text-xs font-medium' style={{ color: textColor }}>
              {formatNumberWithoutCurrency(croresValue)}
            </span>
            <span className='text-[10px] font-normal' style={{ color: textColor }}>
              Crores
            </span>
          </div>
        </div>
      )
    }
    // For gap columns or unknown, render empty cell
    return null
  }

  // Unpinned rows: All visible, sorted, and not pinned
  const mainRows = table.getRowModel().rows.map((row) => (
    <motion.tr
      key={row.id}
      layout='position'
      layoutId={row.id}
      transition={motionTransition}
      className='p-0'
      ref={(el) => {
        if (el) rowRefs.current[row.id] = el
      }}
    >
      <td className={'sticky left-0 bg-white border-b border-r border-[#EBF2F2] z-10 p-[0.5px]'}>
        <div className='flex flex-col'>
          <MFNameCard
            isUsersFund={row.original.isUsersFund}
            title={row.original.name}
            selected={pinnedRowIds.includes(row.original.isin)}
            onBookmarkClick={() => handleRowSelect(row.original.isin)}
            isin={row.original.isin}
            throughFamilyAccess={throughFamilyAccess}
          />
        </div>
      </td>
      {getFlattenedColumns()
        .slice(1)
        .map((col) => {
          if (col.id.startsWith('gap')) {
            return <td key={col.id} className='p-[0.5px]' aria-hidden='true'></td>
          }
          return (
            <td key={col.id} className='p-[0.5px]'>
              {renderCell(row.original, col.id)}
            </td>
          )
        })}
    </motion.tr>
  ))

  // Empty rows at the bottom to fill remaining space
  const emptyRows = useMemo(() => {
    // Use the latest leaf columns (including gaps) so filler rows track current arrangement and sizes
    const leafColumns = table.getAllLeafColumns()

    return Array.from({ length: emptyRowsNeeded }).map((_, rowIdx) => (
      <tr key={`empty-row-${rowIdx}`} className='p-0'>
        {leafColumns.map((col, colIdx) => {
          const sizeStyle = {
            width: col.getSize(),
            minWidth: col.columnDef.minSize,
            maxWidth: col.columnDef.maxSize
          }

          if (col.id.startsWith('gap')) {
            return <td key={`empty-cell-${rowIdx}-${colIdx}`} className='p-[0.5px]' style={sizeStyle} aria-hidden='true'></td>
          }
          if (colIdx === 0) {
            return (
              <td
                key={`empty-cell-${rowIdx}-${colIdx}`}
                className='sticky left-0 bg-[#F6F9F9] border-b border-r border-[#EBF2F2] z-10 p-[0.5px]'
                style={sizeStyle}
              >
                <div className='h-[64px] border-white border'></div>
              </td>
            )
          }
          return (
            <td key={`empty-cell-${rowIdx}-${colIdx}`} className='p-[0.5px]' style={sizeStyle}>
              <div className='h-[64px] bg-[#F6F9F9]'></div>
            </td>
          )
        })}
      </tr>
    ))
  }, [emptyRowsNeeded, table, columnOrder, columnPinning])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const gapWidth = (table.getColumn('gap0')?.getSize() ?? 0) + 64
    const powerRankColumnWidth = table.getColumn('performance')?.getSize() ?? 160
    const scrollLeft = e.currentTarget.scrollLeft

    // Show rank icon when power rank column is scrolled under the name card
    setShowRankInNameCard(scrollLeft > gapWidth)
    setMfnameColumnWidth(scrollLeft > gapWidth ? 200 : 180)
    // Handle FTUX dismissal on user scroll
    handleUserScroll()
  }

  return (
    <LayoutGroup>
      {/* Power Select_B21: live status for sort/filter changes */}
      <div role='status' aria-live='polite' aria-atomic='true' className='sr-only'>
        {statusMessage}
      </div>
      <div
        ref={tableContainerRef}
        onScroll={handleScroll}
        className='scroll-container flex-1 min-h-0 relative overflow-auto no-scrollbar'
        style={{
          height: maxHeight,
          minHeight: maxHeight,
          overscrollBehaviorBlock: 'contain',
          overscrollBehaviorX: 'none',
          overscrollBehaviorY: 'none'
        }}
      >
        <table className='border-separate border-spacing-0 w-full relative'>
          <caption className='sr-only'>
            {`Power Select fund comparison${
              getCategoryLabel(selectedCategoryId)
                ? `, ${getCategoryLabel(selectedCategoryId)}`
                : ''
            }`}
          </caption>
          <thead ref={headerRef} className='sticky top-0 bg-white z-20 shadow-sm'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className='bg-white p-[0.5px]' key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isGap = header.column.id.startsWith('gap')
                  const sortState = header.column.getIsSorted()
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      scope={header.colSpan === 1 ? 'col' : undefined}
                      aria-hidden={isGap ? true : undefined}
                      aria-sort={
                        !isGap && header.column.getCanSort()
                          ? sortState === 'asc'
                            ? 'ascending'
                            : sortState === 'desc'
                              ? 'descending'
                              : 'none'
                          : undefined
                      }
                      className={`${header.column.getIsPinned() === 'left'
                        ? 'sticky left-0 bg-white z-10 border-r border-[#EBF2F2] p-0'
                        : 'p-0'
                        }`}
                      style={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize
                      }}
                    >
                      {header.isPlaceholder ? null : typeof header.column.columnDef.header === 'function' ? (
                        flexRender(header.column.columnDef.header, header.getContext())
                      ) : (
                        <div
                          className={
                            header.column.getCanSort()
                              ? 'text-[13px] cursor-pointer select-none font-normal'
                              : 'text-[13px] font-normal'
                          }
                          onClick={(e) => {
                            // Track column sort with action
                            const currentSortState = header.column.getIsSorted()
                            let sortAction: string
                            if (currentSortState === false) {
                              sortAction = 'Sort Ascending'
                            } else if (currentSortState === 'asc') {
                              sortAction = 'Sort Descending'
                            } else {
                              sortAction = 'Remove Sort'
                            }

                            const columnId = header.column.id || 'Unknown'
                            trackEvent({
                              eventName: EventNames.ButtonTap,
                              payload: {
                                ScreenName: 'PeerAnalyser',
                                buttonLabel: `${columnId} - ${sortAction}`,
                                eventId: selectedCategoryId
                              }
                            })

                            // Call the original sort handler
                            const sortHandler = header.column.getToggleSortingHandler()
                            if (sortHandler) {
                              sortHandler(e)
                            }
                            // Scroll to top
                            if (tableContainerRef.current) {
                              tableContainerRef.current.scrollTop = 0
                            }
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`resizer ${table.getState().columnSizing.isResizing ? 'isResizing' : ''}`}
                        />
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tfoot className='sticky bg-white z-10' style={{ bottom: 0, position: 'sticky' }}>
            {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id} className='p-[0.5px]'>
                {footerGroup.headers.map((header) => (
                  <td
                    key={header.id}
                    colSpan={header.colSpan}
                    aria-hidden={header.column.id.startsWith('gap') ? true : undefined}
                    className={`${header.column.getIsPinned() === 'left' ? 'sticky left-0 bg-white z-20' : ''}`}
                    style={{
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                      position: 'relative'
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    {/* Locked modal centered across eliteScores columns */}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
          {/* Pinned rows visually at the top */}
          <tbody>{pinnedRows}</tbody>
          {/* Main rows in original order, with placeholders for pinned */}
          <tbody>{mainRows}</tbody>
          {/* Empty rows to fill remaining space — decorative fillers, hide from AT */}
          <tbody aria-hidden='true'>{emptyRows}</tbody>
        </table>
      </div>
      {/* FTUX Overlay */}
      {showFtux && (
        <div
          style={{ bottom: `${getBottomPadding() + 155}px` }}
          className={`absolute z-50 left-[205px] items-center justify-center pointer-events-none transition-opacity duration-300 ${isFtuxFading ? 'opacity-0' : 'opacity-100'}`}
        >
          <div className='bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg transform transition-all duration-300 pointer-events-auto'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium'>Swipe to scroll</span>
              <span className='text-sm font-medium'>
                <img className='w-[22px] h-[22px]' src='/scroll_gesture.gif' alt='arrow right' />
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Filter Bottom Sheet */}
      <FilterAndColumnReorderBottomSheet
        isOpen={showFilterBottomSheet}
        onClose={() => setShowFilterBottomSheet(false)}
        columnMetadata={columnMetadata}
        initialColumnOrder={getNonGapColumns(columnOrder)}
        onColumnReorder={handleColumnReorder}
        peerAnalyserDisplayFilters={effectiveDisplayFilters}
      />
    </LayoutGroup>
  )
}
